import React, { useMemo, useEffect } from 'react';
import { Layout, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  DatabaseOutlined,
  PlusOutlined,
  UserOutlined,
  TeamOutlined,
  CloudServerOutlined,
  GiftOutlined as GiftBoxOutlined,
  ControlOutlined as GamepadOutlined,
  DollarOutlined,
  TagsOutlined,
  ToolOutlined,
  BgColorsOutlined as PaletteOutlined,
  BugOutlined,
  WifiOutlined,
  ThunderboltOutlined,
  RocketOutlined,
  KeyOutlined,
  CodeOutlined,
  FormOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';

const { Sider } = Layout;

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const menuItems: MenuProps['items'] = useMemo(() => {
    const baseItems: MenuProps['items'] = [
      {
        key: '/dashboard',
        icon: <DashboardOutlined />,
        label: '控制台',
      },
      {
        key: '/servers',
        icon: <DatabaseOutlined />,
        label: '我的服务器',
      },
      {
        key: 'create-server',
        icon: <PlusOutlined />,
        label: '创建服务器',
        children: [
          {
            key: '/servers/create',
            icon: <RocketOutlined />,
            label: '创建服务器',
          },
          {
            key: '/servers/create-with-activation-code',
            icon: <GiftBoxOutlined />,
            label: '激活码创建',
          },
        ],
      },
      {
        key: '/panel-todo',
        icon: <FormOutlined />,
        label: '面板TODO',
      },
      {
        type: 'divider',
      },
      {
        key: '/profile',
        icon: <UserOutlined />,
        label: '个人中心',
      },
    ];

    // 如果是管理员，添加管理菜单
    if (user?.role === 'admin') {
      baseItems?.push(
        {
          type: 'divider',
        },
        {
          key: '/admin/dashboard',
          icon: <DashboardOutlined />,
          label: '管理概览',
          style: { marginTop: '8px' }
        },
        {
          key: '/admin/users',
          icon: <TeamOutlined />,
          label: '用户管理',
        },
        {
          key: '/admin/servers',
          icon: <CloudServerOutlined />,
          label: '服务器管理',
        },
        {
          key: '/admin/nodes',
          icon: <DatabaseOutlined />,
          label: '节点管理',
        },
        {
          key: '/admin/codes',
          icon: <GiftBoxOutlined />,
          label: '兑换码管理',
        },
        {
          key: '/admin/mod-activation-codes',
          icon: <GiftBoxOutlined />,
          label: 'MOD激活码管理',
        },
        {
          key: '/admin/activation-codes',
          icon: <KeyOutlined />,
          label: '服务器激活码管理',
        },
        {
          key: '/admin/taobao-receipt-codes',
          icon: <GiftBoxOutlined />,
          label: '淘宝收货码管理',
        },
        {
          key: '/admin/games',
          icon: <GamepadOutlined />,
          label: '游戏管理',
        },
        {
          key: '/admin/game-parameter-comments',
          icon: <FormOutlined />,
          label: '游戏参数注释管理',
        },
        {
          key: '/admin/pricing',
          icon: <DollarOutlined />,
          label: '价格管理',
        },
        {
          key: '/admin/coupons',
          icon: <TagsOutlined />,
          label: '优惠码管理',
        },
        {
          key: '/admin/themes',
          icon: <PaletteOutlined />,
          label: '主题管理',
        },
        {
          key: '/admin/settings',
          icon: <ToolOutlined />,
          label: '系统设置',
        },
        {
          key: '/admin/version-management',
          icon: <CodeOutlined />,
          label: '版本管理',
        },
        {
          key: '/admin/error-monitor',
          icon: <BugOutlined />,
          label: '错误监控',
        },
        {
          key: '/admin/simple-errors',
          icon: <BugOutlined />,
          label: '简单错误',
        },
        {
          key: '/socket-diagnostic',
          icon: <WifiOutlined />,
          label: 'Socket诊断',
        },
        {
          key: '/socket-test',
          icon: <ThunderboltOutlined />,
          label: 'Socket测试',
        }
      );
    }

    return baseItems;
  }, [user?.role]);

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  // 获取当前选中的菜单项
  const getSelectedKeys = () => {
    const path = location.pathname;
    return [path];
  };

  // 动态调整子菜单样式
  useEffect(() => {
    const adjustSubmenuStyles = () => {
      // 处理内联模式的子菜单项，确保它们有足够高的z-index
      const submenuItems = document.querySelectorAll('.ant-menu-inline .ant-menu-sub .ant-menu-item');
      submenuItems.forEach((item) => {
        const element = item as HTMLElement;
        element.style.zIndex = '10000';
        element.style.position = 'relative';
        element.style.backgroundColor = 'white';
        element.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
      });
    };

    // 初始调整
    adjustSubmenuStyles();

    // 监听DOM变化
    const observer = new MutationObserver(adjustSubmenuStyles);
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, []);

  return (
    <Sider
      width={256}
      className="custom-sidebar d-md-block d-sm-none"
      style={{
        position: 'fixed',
        left: 0,
        top: '64px',
        height: 'calc(100vh - 64px)',
        zIndex: 50,
        display: 'block',
        overflow: 'visible',
        overflowY: 'auto'
      }}
      theme="light"
    >
      <div style={{ padding: '16px' }}>
        <Menu
          mode="inline"
          selectedKeys={getSelectedKeys()}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ border: 'none' }}
          subMenuOpenDelay={0}
          subMenuCloseDelay={0}
          getPopupContainer={() => document.body}
        />
      </div>
    </Sider>
  );
};

export default Sidebar;



